<script lang="ts" setup>
import { Dayjs } from 'dayjs'
import { computed } from 'vue'

import StayFilters from '@/common/StayFilters.vue'
import { useStore } from '@/store'

import { ChartUnit } from './types'

const store = useStore()

const props = withDefaults(defineProps<{
  earlyLimit: Dayjs
  lateLimit: Dayjs
  chartUnit?: ChartUnit
  areIntervalsDisplayed?: boolean
  isDialysisMode?: boolean
}>(), {
  chartUnit: ChartUnit.MONTH,
  areIntervalsDisplayed: true,
  isDialysisMode: false,
})

const emit = defineEmits<{
  'set-date-range': [range: { earlyLimit: Dayjs | null, lateLimit: Dayjs | null }]
  'set-selected-medical-units': [medicalUnitList: number[]]
  'set-chart-unit': [unit: ChartUnit]
  'set-hospitalization-types': [types: number[]]
}>()

const medicalUnits = computed(() => props.isDialysisMode ? store.getters.dialysisMedicalUnits : store.state.settings.medicalUnits)
const selectedMedicalUnits = computed(() => store.state.settings.selectedMedicalUnits || [])
const medicalUnitsRequest = computed(() => store.state.remoteLists.medicalUnitsRequest)

const handleDateRangeChange = (range: { earlyLimit: Dayjs | null, lateLimit: Dayjs | null }) => {
  emit('set-date-range', range)
}

const handleMedicalUnitsChange = (medicalUnitList: number[]) => {
  emit('set-selected-medical-units', medicalUnitList)
}

const setChartUnit = (unit: ChartUnit) => {
  emit('set-chart-unit', unit)
}

const handleHospitalizationTypesChange = (types: number[]) => {
  emit('set-hospitalization-types', types)
}
</script>

<template>
  <div class="col mx-1 align-items-center text-start">
    <div class="row align-items-center">
      <stay-filters
        :early-limit="earlyLimit"
        :late-limit="lateLimit"
        :selected-medical-units="selectedMedicalUnits"
        :medical-units="medicalUnits"
        :medical-units-request="medicalUnitsRequest"
        @update:date-range="handleDateRangeChange"
        @update:selected-medical-units="handleMedicalUnitsChange"
        @update:hospitalization-types="handleHospitalizationTypesChange"
      />
    </div>
    <div
      v-if="areIntervalsDisplayed"
      class="col-12 col-lg-5 ms-auto row intervals justify-content-lg-end justify-content-center"
    >
      <div class="col-1" />
      <div class="col-auto row align-items-center">
        <div class="col-auto">
          {{ $t('time.interval') }} :
        </div>
        <div class="col-auto pt-1">
          <label class="row g-0 align-items-center">
            <span class="col">
              <input
                id="week_interval"
                type="radio"
                :checked="chartUnit === ChartUnit.WEEK"
                class="me-1"
                @input="() => setChartUnit(ChartUnit.WEEK)"
              >
              {{ $t('time.week') }}
            </span>
          </label>
        </div>
        <div class="col-auto pt-1">
          <label class="row g-0 align-items-center">
            <span class="col">
              <input
                id="month_interval"
                type="radio"
                :checked="chartUnit === ChartUnit.MONTH"
                class="me-1"
                @input="() => setChartUnit(ChartUnit.MONTH)"
              >
              {{ $t('time.month') }}
            </span>
          </label>
        </div>
        <div class="col-auto pt-1">
          <label class="row g-0 align-items-center">
            <span class="col">
              <input
                id="year_interval"
                type="radio"
                :checked="chartUnit === ChartUnit.YEAR"
                class="me-1"
                @input="() => setChartUnit(ChartUnit.YEAR)"
              >
              {{ $t('time.year') }}
            </span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
